import { Chart, ChartEvent, LegendElement, LegendItem, Plugin } from 'chart.js';

export function legendOnHover(_event: ChartEvent, legendItem: LegendItem, legend: LegendElement<any>): void {
  const chart = legend.chart;
  (chart as any)._hoveredLegendIndex = legendItem.datasetIndex ?? -1;
  chart.canvas.style.cursor = 'pointer';
  chart.render();
}

export function legendOnLeave(_event: ChartEvent, _legendItem: LegendItem, legend: LegendElement<any>): void {
  const chart = legend.chart;
  (chart as any)._hoveredLegendIndex = -1;
  chart.canvas.style.cursor = 'default';
  chart.render();
}

function getHoveredIndex(chart: Chart): number {
  const legendHover: number = (chart as any)._hoveredLegendIndex ?? -1;
  const chartHover: number = (chart as any)._hoveredChartDatasetIndex ?? -1;
  return legendHover >= 0 ? legendHover : chartHover;
}

function applyAlpha(color: string, alpha: number): string {
  if (color.startsWith('#')) {
    const r = parseInt(color.slice(1, 3), 16);
    const g = parseInt(color.slice(3, 5), 16);
    const b = parseInt(color.slice(5, 7), 16);
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
  }
  const match = color.match(/rgba?\((\d+),\s*(\d+),\s*(\d+)/);
  if (match) {
    return `rgba(${match[1]}, ${match[2]}, ${match[3]}, ${alpha})`;
  }
  return color;
}

interface LegendStyleSnapshot {
  fontColor: string | undefined;
  fillStyle: string | undefined;
  strokeStyle: string | undefined;
}

export const legendHighlightPlugin: Plugin = {
  id: 'legendHighlight',

  afterEvent(chart: Chart, args) {
    const active = chart.getActiveElements();
    const newIndex = active.length > 0 ? active[0]!.datasetIndex : -1;
    const oldIndex: number = (chart as any)._hoveredChartDatasetIndex ?? -1;

    if (newIndex !== oldIndex) {
      (chart as any)._hoveredChartDatasetIndex = newIndex;
      args.changed = true;
    }
  },

  beforeDraw(chart: Chart) {
    const hoveredIndex = getHoveredIndex(chart);
    if (hoveredIndex < 0) return;

    const legend = chart.legend;
    if (!legend?.legendItems) return;

    const originals: LegendStyleSnapshot[] = legend.legendItems.map((item) => ({
      fontColor: item.fontColor as string | undefined,
      fillStyle: item.fillStyle as string | undefined,
      strokeStyle: item.strokeStyle as string | undefined,
    }));
    (chart as any)._originalLegendStyles = originals;

    for (const item of legend.legendItems) {
      if (item.datasetIndex !== hoveredIndex) {
        if (typeof item.fontColor === 'string') {
          item.fontColor = applyAlpha(item.fontColor, 0.2);
        }
        if (typeof item.fillStyle === 'string') {
          item.fillStyle = applyAlpha(item.fillStyle, 0.2);
        }
        if (typeof item.strokeStyle === 'string') {
          item.strokeStyle = applyAlpha(item.strokeStyle, 0.2);
        }
      }
    }
  },

  afterDraw(chart: Chart) {
    const originals: LegendStyleSnapshot[] | undefined = (chart as any)._originalLegendStyles;
    if (!originals) return;

    const legend = chart.legend;
    if (!legend?.legendItems) return;

    legend.legendItems.forEach((item, i) => {
      const original = originals[i];
      if (!original) return;
      item.fontColor = original.fontColor;
      item.fillStyle = original.fillStyle;
      item.strokeStyle = original.strokeStyle;
    });

    delete (chart as any)._originalLegendStyles;
  },

  beforeDatasetDraw(chart: Chart, args: { index: number }) {
    const hoveredIndex = getHoveredIndex(chart);
    if (hoveredIndex < 0) return;

    if (args.index !== hoveredIndex) {
      chart.ctx.globalAlpha = 0.2;
    }
  },

  afterDatasetDraw(chart: Chart, args: { index: number }) {
    const hoveredIndex = getHoveredIndex(chart);
    if (hoveredIndex < 0) return;

    if (args.index !== hoveredIndex) {
      chart.ctx.globalAlpha = 1;
    }
  },
};
